/**
 * Offline audio cache. When a book is pinned, we pre-render every sentence
 * through the laptop /tts endpoint and store the WAV blob in IndexedDB.
 * Playback (kokoro-server.ts) checks this cache first so pinned books
 * play with zero dependency on the laptop being alive.
 *
 * Key format: `${bookId}::${chapterIdx}::${sentenceIdx}::${voice}`
 * Speed is not keyed - we cache at speed=1.0 and apply playbackRate at
 * runtime (small pitch drift at non-1x, acceptable for audiobook use).
 */

import { getDB, getChapters, putChapters, type LocalBook } from './db';
import { getServerUrl } from './kokoro-server';
import { supabase, supabaseEnabled } from './supabase';

// Kokoro server rejects short-form voice ids ("bella") - needs the full
// prefix ("af_bella"). Keep in sync with VOICE_MAP in kokoro-server.ts.
const VOICE_MAP: Record<string, string> = {
  bella: 'af_bella', michael: 'am_michael', nicole: 'af_nicole',
  sarah: 'af_sarah', adam: 'am_adam', emma: 'bf_emma',
};
function resolveVoiceId(v: string): string {
  return VOICE_MAP[v] || (v.includes('_') ? v : 'af_bella');
}

const SENTENCE_SPLIT = /(?<=[.!?])\s+(?=[A-Z"\u2018\u201C])/;

export function splitChapterSentences(text: string): string[] {
  return text.replace(/\s+/g, ' ').split(SENTENCE_SPLIT).map((s) => s.trim()).filter(Boolean);
}

export function audioKey(bookId: string, ch: number, sent: number, voice: string): string {
  return `${bookId}::${ch}::${sent}::${voice}`;
}

export async function getCachedAudio(bookId: string, ch: number, sent: number, voice: string): Promise<Blob | null> {
  const db = await getDB();
  const rec = await db.get('audio', audioKey(bookId, ch, sent, voice));
  return rec?.blob ?? null;
}

export async function putCachedAudio(bookId: string, ch: number, sent: number, voice: string, blob: Blob): Promise<void> {
  const db = await getDB();
  await db.put('audio', {
    key: audioKey(bookId, ch, sent, voice),
    book_id: bookId,
    ch, sent, voice,
    bytes: blob.size,
    blob,
  });
}

export async function deleteBookAudio(bookId: string): Promise<number> {
  const db = await getDB();
  const keys = await db.getAllKeysFromIndex('audio', 'by-book', bookId);
  const tx = db.transaction('audio', 'readwrite');
  await Promise.all(keys.map((k) => tx.store.delete(k)));
  await tx.done;
  return keys.length;
}

export async function getBookAudioStats(bookId: string): Promise<{ count: number; bytes: number }> {
  const db = await getDB();
  const all = await db.getAllFromIndex('audio', 'by-book', bookId);
  return {
    count: all.length,
    bytes: all.reduce((a, r) => a + (r.bytes || 0), 0),
  };
}

export async function getAllAudioStats(): Promise<{ count: number; bytes: number }> {
  const db = await getDB();
  const all = await db.getAll('audio');
  return {
    count: all.length,
    bytes: all.reduce((a, r) => a + (r.bytes || 0), 0),
  };
}

export type DownloadProgress = {
  status: 'init' | 'downloading' | 'done' | 'error';
  done: number;
  total: number;
  currentChapter?: number;
  currentChapterTitle?: string;
  error?: string;
};

export async function estimateStorage(): Promise<{ usage: number; quota: number; pct: number }> {
  try {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      const e = await navigator.storage.estimate();
      const usage = e.usage || 0;
      const quota = e.quota || 0;
      return { usage, quota, pct: quota ? usage / quota : 0 };
    }
  } catch {}
  return { usage: 0, quota: 0, pct: 0 };
}

/** Fetch chapters from Supabase into local IndexedDB. Used when a book
 *  appears in the library via the SiYuan mirror but its chapter text
 *  hasn't been cached locally yet (e.g. after site-data clear). */
async function ensureLocalChapters(book: LocalBook, onProgress: (p: DownloadProgress) => void): Promise<boolean> {
  if (!supabaseEnabled) return false;
  onProgress({ status: 'init', done: 0, total: 0, currentChapterTitle: 'Fetching chapters from Supabase...' });
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;
    const { data, error } = await supabase
      .from('chapters')
      .select('idx, title, text')
      .eq('user_id', user.id)
      .eq('book_id', book.id)
      .order('idx', { ascending: true });
    if (error || !data || !data.length) return false;
    await putChapters(book.id, data.map((c: any) => ({ idx: c.idx, title: c.title, text: c.text })));
    return true;
  } catch (e) {
    console.warn('[offline-audio] ensureLocalChapters failed', e);
    return false;
  }
}

export async function downloadBookOffline(
  book: LocalBook,
  voice: string,
  onProgress: (p: DownloadProgress) => void,
  isActive: () => boolean = () => true,
): Promise<void> {
  onProgress({ status: 'init', done: 0, total: 0 });
  let chapters = await getChapters(book.id);
  if (!chapters.length) {
    // Try to pull chapters from Supabase before giving up
    const ok = await ensureLocalChapters(book, onProgress);
    if (ok) chapters = await getChapters(book.id);
  }
  if (!chapters.length) {
    onProgress({ status: 'error', done: 0, total: 0, error: 'No chapters found. Re-upload this book from the Doomscroll app or tap Rescan on the file.' });
    return;
  }

  // Pre-compute full sentence index so total is known up front
  const flat: { ch: number; sent: number; text: string; title: string }[] = [];
  for (const c of chapters) {
    const sents = splitChapterSentences(c.text || '');
    sents.forEach((t, si) => flat.push({ ch: c.idx, sent: si, text: t, title: c.title || `Chapter ${c.idx + 1}` }));
  }
  const total = flat.length;
  onProgress({ status: 'downloading', done: 0, total });

  const serverUrl = getServerUrl();
  const voiceId = resolveVoiceId(voice);
  const db = await getDB();
  let done = 0;
  let failed = 0;

  // Skip any sentence already cached (idempotent resume).
  const existingKeys = new Set(await db.getAllKeysFromIndex('audio', 'by-book', book.id));

  // Single background worker. Keeps server headroom for the Reader's own
  // LOOKAHEAD=3 playback fetches - higher offline concurrency starves
  // playback on the Python GIL-bound Kokoro server.
  const CONCURRENCY = 1;
  let idx = 0;

  async function worker() {
    while (true) {
      if (!isActive()) return;
      // Yield to Reader playback. If a reader playback session is active
      // we pause background downloads until it finishes.
      while ((window as any).__doomReaderPlaying && isActive()) {
        await new Promise((r) => setTimeout(r, 1500));
      }
      if (!isActive()) return;
      const i = idx++;
      if (i >= flat.length) return;
      const item = flat[i];
      // Cache keyed by voiceId (full form) so offline playback lookup matches
      const key = audioKey(book.id, item.ch, item.sent, voiceId);
      if (existingKeys.has(key)) {
        done++;
        if (done % 5 === 0 || done === total) {
          onProgress({ status: 'downloading', done, total, currentChapter: item.ch, currentChapterTitle: item.title });
        }
        continue;
      }
      try {
        const res = await fetch(serverUrl + '/tts', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: item.text, voice: voiceId, speed: 1.0 }),
        });
        if (!res.ok) throw new Error('HTTP ' + res.status);
        const buf = await res.arrayBuffer();
        const blob = new Blob([buf], { type: 'audio/wav' });
        await putCachedAudio(book.id, item.ch, item.sent, voiceId, blob);
        done++;
        if (done % 3 === 0 || done === total) {
          onProgress({ status: 'downloading', done, total, currentChapter: item.ch, currentChapterTitle: item.title });
        }
      } catch (e: any) {
        // Keep going - single-sentence failure shouldn't abort the whole book.
        console.warn('[offline-audio] skip', key, e?.message || e);
        failed++;
      }
    }
  }

  await Promise.all(Array.from({ length: CONCURRENCY }, () => worker()));

  if (!isActive()) return;
  if (done === 0 && failed > 0) {
    onProgress({ status: 'error', done: 0, total, error: `All ${failed} fetches failed. Laptop TTS reachable?` });
    return;
  }
  onProgress({ status: 'done', done, total });
}
